import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Group as GroupIcon } from '@mui/icons-material'
import { userGroupService, CreateUserGroupRequest } from '../../services/userGroupService'

interface UserGroupCreateModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const UserGroupCreateModal: React.FC<UserGroupCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateUserGroupRequest>({
    name: '',
    description: '',
    department: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleInputChange =
    (field: keyof CreateUserGroupRequest) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }))
      // エラーをクリア
      if (error) setError(null)
    }

  const handleSubmit = async () => {
    // バリデーション
    if (!formData.name.trim()) {
      setError('グループ名は必須です')
      return
    }

    if (formData.name.length > 100) {
      setError('グループ名は100文字以下で入力してください')
      return
    }

    if (formData.description && formData.description.length > 500) {
      setError('説明は500文字以下で入力してください')
      return
    }

    if (formData.department && formData.department.length > 100) {
      setError('部門名は100文字以下で入力してください')
      return
    }

    try {
      setLoading(true)
      setError(null)

      // 空文字列をundefinedに変換
      const requestData: CreateUserGroupRequest = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        department: formData.department?.trim() || undefined,
      }

      await userGroupService.createUserGroup(requestData)

      // フォームをリセット
      setFormData({
        name: '',
        description: '',
        department: '',
      })

      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'グループの作成に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return

    setFormData({
      name: '',
      description: '',
      department: '',
    })
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon />
          新規ユーザーグループ作成
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            チーム単位でのコンプライアンス教育を効率的に管理するためのグループを作成します。
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="グループ名"
            value={formData.name}
            onChange={handleInputChange('name')}
            margin="normal"
            required
            disabled={loading}
            error={formData.name.length > 100}
            helperText={`${formData.name.length}/100文字`}
            placeholder="例: 開発チーム、営業部グループ"
          />

          <TextField
            fullWidth
            label="説明"
            value={formData.description}
            onChange={handleInputChange('description')}
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
            error={(formData.description?.length || 0) > 500}
            helperText={`${formData.description?.length || 0}/500文字（任意）`}
            placeholder="グループの目的や特徴を説明してください"
          />

          <TextField
            fullWidth
            label="部門"
            value={formData.department}
            onChange={handleInputChange('department')}
            margin="normal"
            disabled={loading}
            error={(formData.department?.length || 0) > 100}
            helperText={`${formData.department?.length || 0}/100文字（任意）`}
            placeholder="例: 開発部、営業部、総務部"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? '作成中...' : 'グループ作成'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserGroupCreateModal
